// src/pages/Akkoord.js
// vAKK-006
//
// Publieke akkoordpagina voor StaffIQ.
// - Terug naar StaffIQ-knop toegevoegd voor mobiel
// - Akkoordtekst kandidaat spreekt niet meer over namens organisatie
// Deze pagina is bereikbaar zonder inloggen via:
// /akkoord/:token
// /approval/:token

import React, { useEffect, useMemo, useState } from "react";
import { useParams } from "react-router-dom";

function safeText(value, fallback = "") {
  if (value === null || value === undefined) return fallback;
  return String(value);
}

function statusLabel(status) {
  const s = safeText(status, "").toLowerCase();

  if (s === "sent") return "Verzonden";
  if (s === "viewed") return "Bekeken";
  if (s === "approved") return "Akkoord";
  if (s === "rejected") return "Niet akkoord";
  if (s === "expired") return "Verlopen";

  return "Concept";
}

function documentTypeLabel(type) {
  const t = safeText(type, "").toLowerCase();

  if (t === "order") return "Opdracht";
  if (t === "invoice") return "Factuur";
  if (t === "placement_customer") return "Plaatsingsbevestiging klant";
  if (t === "placement_candidate") return "Plaatsingsbevestiging kandidaat";

  return "Offerte";
}

function channelLabel(channel) {
  const c = safeText(channel, "").toLowerCase();

  if (c === "whatsapp") return "WhatsApp";
  if (c === "email") return "E-mail";
  if (c === "post") return "Post";

  return "Handmatig";
}

function buildApiUrl(path) {
  const p = safeText(path, "").trim();
  if (!p) return "";

  if (/^https?:\/\//i.test(p)) return p;

  const origin = window.location.origin || "";
  const isLocalFrontend =
    window.location.hostname === "localhost" &&
    window.location.port === "3000";

  if (isLocalFrontend && p.startsWith("/api/")) {
    return `http://localhost:3001${p}`;
  }

  return `${origin}${p.startsWith("/") ? p : `/${p}`}`;
}

function returnPathForApproval(approval) {
  const t = safeText(approval?.document_type, "").toLowerCase();

  if (t.startsWith("placement_")) return "/plaatsingen";
  if (t === "invoice") return "/facturen";
  if (t === "offer" || t === "order") return "/offertes";

  return "/";
}

function authorityTextForApproval(approval) {
  const t = safeText(approval?.document_type, "").toLowerCase();

  if (t === "placement_candidate") {
    return "Ik ga akkoord met dit document.";
  }

  return "Ik ben bevoegd om namens deze organisatie akkoord te geven en ik ga akkoord met dit document.";
}

export default function Akkoord() {
  const { token } = useParams();

  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState("");
  const [approval, setApproval] = useState(null);

  const [name, setName] = useState("");
  const [email, setEmail] = useState("");
  const [authorised, setAuthorised] = useState(false);
  const [remarks, setRemarks] = useState("");

  const isFinal = useMemo(() => {
    const s = safeText(approval?.status, "").toLowerCase();
    return ["approved", "rejected", "expired"].includes(s);
  }, [approval?.status]);

  const authorityError = useMemo(() => {
    const msg = safeText(error, "").trim();
    if (!msg) return "";
    const lower = msg.toLowerCase();
    return lower.includes("bevoegd") || lower.includes("akkoord te geven") ? msg : "";
  }, [error]);

  const nameError = useMemo(() => {
    const msg = safeText(error, "").trim();
    if (!msg) return "";
    const lower = msg.toLowerCase();
    return lower.includes("naam") && lower.includes("verplicht") ? msg : "";
  }, [error]);

  const emailError = useMemo(() => {
    const msg = safeText(error, "").trim();
    if (!msg) return "";
    const lower = msg.toLowerCase();
    return (lower.includes("e-mail") || lower.includes("email")) && lower.includes("verplicht") ? msg : "";
  }, [error]);

  const bottomError = useMemo(() => {
    const msg = safeText(error, "").trim();
    if (!msg || authorityError) return "";
    return msg;
  }, [error, authorityError]);

  async function loadApproval() {
    try {
      setLoading(true);
      setError("");

      const res = await fetch(buildApiUrl(`/api/approvals/${encodeURIComponent(token)}`));
      const data = await res.json().catch(() => ({}));

      if (!res.ok || !data?.ok) {
        throw new Error(data?.error || "Akkoordlink kon niet worden geladen.");
      }

      const row = data.approval || null;
      setApproval(row);

      if (row?.contact_name && !name) setName(row.contact_name);
      if (row?.contact_email && !email) setEmail(row.contact_email);
    } catch (err) {
      setError(err?.message || "Akkoordlink kon niet worden geladen.");
      setApproval(null);
    } finally {
      setLoading(false);
    }
  }

  useEffect(() => {
    loadApproval();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [token]);

  async function submitDecision(decision) {
    try {
      setSaving(true);
      setError("");

      const isApprove = decision === "approve";
      const res = await fetch(buildApiUrl(`/api/approvals/${encodeURIComponent(token)}/${isApprove ? "approve" : "reject"}`), {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
        },
        body: JSON.stringify({
          name,
          email,
          authorised,
          remarks,
        }),
      });

      const data = await res.json().catch(() => ({}));

      if (!res.ok || !data?.ok) {
        throw new Error(data?.error || "Opslaan mislukt.");
      }

      setApproval(data.approval || approval);
    } catch (err) {
      setError(err?.message || "Opslaan mislukt.");
    } finally {
      setSaving(false);
    }
  }

  const pageStyle = {
    minHeight: "100vh",
    background: "#f4f6f8",
    padding: "24px 12px",
    boxSizing: "border-box",
    fontFamily: "Arial, Helvetica, sans-serif",
    color: "#111",
  };

  const cardStyle = {
    width: "min(760px, 100%)",
    margin: "0 auto",
    background: "white",
    borderRadius: 14,
    boxShadow: "0 10px 35px rgba(0,0,0,0.12)",
    padding: 22,
    boxSizing: "border-box",
    overflow: "hidden",
  };

  const inputStyle = {
    width: "100%",
    padding: "10px 12px",
    border: "1px solid #d0d7de",
    borderRadius: 6,
    boxSizing: "border-box",
    fontSize: 15,
    background: "white",
  };

  const buttonBase = {
    padding: "11px 16px",
    border: "none",
    borderRadius: 7,
    color: "white",
    fontSize: 15,
    fontWeight: 700,
    cursor: saving ? "not-allowed" : "pointer",
    opacity: saving ? 0.6 : 1,
  };

  if (loading) {
    return (
      <div style={pageStyle}>
        <div style={cardStyle}>Akkoordlink wordt geladen...</div>
      </div>
    );
  }

  if (error && !approval) {
    return (
      <div style={pageStyle}>
        <div style={cardStyle}>
          <h1 style={{ marginTop: 0 }}>Akkoordlink</h1>
          <div style={{ color: "crimson" }}>{error}</div>
        </div>
      </div>
    );
  }

  return (
    <div style={pageStyle}>
      <div style={cardStyle}>
        <h1 style={{ marginTop: 0, marginBottom: 4 }}>
          {documentTypeLabel(approval?.document_type)} digitaal accorderen
        </h1>

        <div style={{ opacity: 0.7, marginBottom: 18 }}>
          Status: <b>{statusLabel(approval?.status)}</b>
        </div>

        <div style={{ marginBottom: 18, display: "flex", gap: 10, flexWrap: "wrap" }}>
          <button
            type="button"
            onClick={() => {
              window.location.href = returnPathForApproval(approval);
            }}
            style={{
              padding: "9px 13px",
              border: "1px solid #d0d7de",
              borderRadius: 7,
              background: "#fff",
              color: "#1f4e79",
              fontWeight: 700,
              cursor: "pointer",
            }}
          >
            Terug naar StaffIQ
          </button>
        </div>

        {error && !authorityError && (
          <div
            style={{
              marginBottom: 14,
              padding: "10px 12px",
              border: "1px solid #f2b8b8",
              background: "#fff0f0",
              color: "#9b1c1c",
              borderRadius: 6,
            }}
          >
            {error}
          </div>
        )}

        <div
          style={{
            display: "grid",
            gridTemplateColumns: "160px 1fr",
            gap: "8px 12px",
            marginBottom: 18,
            lineHeight: 1.45,
          }}
        >
          <div style={{ fontWeight: 700 }}>Document</div>
          <div>
            {documentTypeLabel(approval?.document_type)} {approval?.document_number || ""}
          </div>

          <div style={{ fontWeight: 700 }}>Omschrijving</div>
          <div>{approval?.document_title || "-"}</div>

          <div style={{ fontWeight: 700 }}>Organisatie</div>
          <div>{approval?.organisation_name || "-"}</div>

          <div style={{ fontWeight: 700 }}>Contactpersoon</div>
          <div>{approval?.contact_name || "-"}</div>
        </div>

        {approval?.document_summary && (
          <div
            style={{
              padding: "12px 14px",
              background: "#f6f8fa",
              border: "1px solid #d8dee4",
              borderRadius: 8,
              marginBottom: 18,
              lineHeight: 1.5,
              whiteSpace: "pre-wrap",
              overflowWrap: "anywhere",
              wordBreak: "break-word",
            }}
          >
            <div style={{ fontWeight: 700, marginBottom: 6 }}>Beknopte inhoud</div>
            {approval.document_summary}
          </div>
        )}

        <div
          style={{
            padding: "10px 12px",
            background: "#fff",
            border: "1px solid #e5e7eb",
            borderRadius: 8,
            marginBottom: 18,
            lineHeight: 1.5,
            fontSize: 14,
          }}
        >
          <div><b>Verzonden via:</b> {channelLabel(approval?.channel)}</div>
          <div><b>Verzonden door:</b> {approval?.sent_by_name || "StaffIQ"}{approval?.sent_by_email ? ` (${approval.sent_by_email})` : ""}</div>
          <div><b>Ontvangen door:</b> {approval?.sent_to_name || approval?.organisation_name || "-"}{approval?.sent_to ? ` (${approval.sent_to})` : ""}</div>
        </div>

        {approval?.has_pdf && approval?.pdf_url && (
          <div style={{ marginBottom: 18 }}>
            <a
              href={buildApiUrl(approval.pdf_url)}
              target="_blank"
              rel="noreferrer"
              style={{
                display: "inline-block",
                padding: "10px 14px",
                background: "#1f4e79",
                color: "white",
                borderRadius: 7,
                textDecoration: "none",
                fontWeight: 700,
              }}
            >
              PDF bekijken
            </a>
          </div>
        )}

        <div
          style={{
            padding: "12px 14px",
            background: "#f6f8fa",
            border: "1px solid #d8dee4",
            borderRadius: 8,
            marginBottom: 18,
            lineHeight: 1.5,
          }}
        >
          {approval?.approval_text ||
            "Ik verklaar bevoegd te zijn en geef akkoord op dit document."}
        </div>

        {isFinal ? (
          <div
            style={{
              padding: "14px",
              background: approval?.status === "approved" ? "#eaf7ea" : "#fff8e5",
              border: "1px solid #d8dee4",
              borderRadius: 8,
              lineHeight: 1.5,
            }}
          >
            {approval?.status === "approved" ? (
              <>
                <b>Akkoord ontvangen.</b>
                <br />
                Akkoord gegeven door: {approval?.approved_name || "-"}
                <br />
                E-mail: {approval?.approved_email || "-"}
                <br />
                Verzonden door: {approval?.sent_by_name || "StaffIQ"}{approval?.sent_by_email ? ` (${approval.sent_by_email})` : ""}
                <br />
                Ontvangen door: {approval?.sent_to_name || approval?.organisation_name || "-"}{approval?.sent_to ? ` (${approval.sent_to})` : ""}
              </>
            ) : approval?.status === "rejected" ? (
              <>
                <b>Niet akkoord geregistreerd.</b>
                <br />
                Niet akkoord door: {approval?.rejected_name || "-"}
                <br />
                E-mail: {approval?.rejected_email || "-"}
                <br />
                Verzonden door: {approval?.sent_by_name || "StaffIQ"}{approval?.sent_by_email ? ` (${approval.sent_by_email})` : ""}
                <br />
                Ontvangen door: {approval?.sent_to_name || approval?.organisation_name || "-"}{approval?.sent_to ? ` (${approval.sent_to})` : ""}
                <br />
                Opmerking: {approval?.remarks || "-"}
              </>
            ) : (
              <b>Deze akkoordlink is verlopen.</b>
            )}
          </div>
        ) : (
          <div style={{ display: "grid", gap: 12 }}>
            <label>
              <div style={{ fontSize: 13, fontWeight: 700, marginBottom: 4 }}>Naam</div>
              <input
                value={name}
                onChange={(e) => setName(e.target.value)}
                style={{
                  ...inputStyle,
                  border: nameError ? "2px solid #b42318" : inputStyle.border,
                  background: nameError ? "#fff7f7" : inputStyle.background,
                }}
                autoComplete="name"
              />
              {nameError && (
                <div
                  style={{
                    marginTop: 6,
                    color: "#9b1c1c",
                    fontWeight: 700,
                    fontSize: 13,
                  }}
                >
                  {nameError}
                </div>
              )}
            </label>

            <label>
              <div style={{ fontSize: 13, fontWeight: 700, marginBottom: 4 }}>E-mail</div>
              <input
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                style={{
                  ...inputStyle,
                  border: emailError ? "2px solid #b42318" : inputStyle.border,
                  background: emailError ? "#fff7f7" : inputStyle.background,
                }}
                autoComplete="email"
                inputMode="email"
              />
              {emailError && (
                <div
                  style={{
                    marginTop: 6,
                    color: "#9b1c1c",
                    fontWeight: 700,
                    fontSize: 13,
                  }}
                >
                  {emailError}
                </div>
              )}
            </label>

            <label>
              <div style={{ fontSize: 13, fontWeight: 700, marginBottom: 4 }}>
                Opmerking
              </div>
              <textarea
                value={remarks}
                onChange={(e) => setRemarks(e.target.value)}
                rows={4}
                style={{ ...inputStyle, resize: "vertical" }}
              />
            </label>

            <div>
              <label
                style={{
                  display: "grid",
                  gridTemplateColumns: "24px 1fr",
                  gap: 8,
                  alignItems: "start",
                  lineHeight: 1.45,
                }}
              >
                <input
                  type="checkbox"
                  checked={authorised}
                  onChange={(e) => setAuthorised(e.target.checked)}
                  style={{ marginTop: 3 }}
                />
                <span>
                  {authorityTextForApproval(approval)}
                </span>
              </label>

              {authorityError && (
                <div
                  style={{
                    marginTop: 8,
                    marginLeft: 32,
                    padding: "9px 11px",
                    border: "1px solid #f0b429",
                    background: "#fff8e1",
                    color: "#7a4d00",
                    borderRadius: 6,
                    fontWeight: 700,
                  }}
                >
                  {authorityError}
                </div>
              )}
            </div>

            {bottomError && (
              <div
                style={{
                  padding: "11px 13px",
                  border: "1px solid #f2b8b8",
                  background: "#fff0f0",
                  color: "#9b1c1c",
                  borderRadius: 7,
                  fontWeight: 700,
                  lineHeight: 1.45,
                }}
              >
                {bottomError}
              </div>
            )}

            <div style={{ display: "flex", gap: 10, flexWrap: "wrap", marginTop: 4 }}>
              <button
                type="button"
                onClick={() => submitDecision("approve")}
                disabled={saving}
                style={{ ...buttonBase, background: "#2f7d32" }}
              >
                Akkoord geven
              </button>

              <button
                type="button"
                onClick={() => submitDecision("reject")}
                disabled={saving}
                style={{ ...buttonBase, background: "#b42318" }}
              >
                Niet akkoord
              </button>
            </div>
          </div>
        )}
      </div>
    </div>
  );
}
